import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta, type PageRequest } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { DEFAULT_PROMOTION_PAGE_SIZE } from '../constants/promotion.constants';
import {
  promoDistributionResponseSchema,
  promoInstituteListSchema,
  promoItemResponseSchema,
  promoItemTypeResponseSchema,
  promoMemberListSchema,
  userPromoResponseSchema,
  type PromoAdjustmentSearch,
  type PromoAssignmentParams,
  type PromoCatalogueParams,
  type PromoDistributionResponse,
  type PromoDistributionRow,
  type PromoDrillParams,
  type PromoInstitute,
  type PromoItemPayload,
  type PromoItemResponse,
  type PromoItemParams,
  type PromoItemTypePayload,
  type PromoItemTypeResponse,
  type PromoMember,
  type UserPromoResponse,
} from '../types/promotion.types';

export const promotionQueryKeys = {
  all: () => ['promotion'] as const,
  adjustment: (params: PromoAdjustmentSearch) =>
    [...promotionQueryKeys.all(), 'adjustment', params] as const,
  distributedItem: (promoItemId: number, params: PromoDrillParams) =>
    [...promotionQueryKeys.all(), 'distributed-item', params, promoItemId] as const,
  members: (promoItemId: number, params: PromoDrillParams) =>
    [...promotionQueryKeys.all(), 'members', params, promoItemId] as const,
  institutes: (promoItemId: number, userId: number, params: PromoDrillParams) =>
    [...promotionQueryKeys.all(), 'institutes', params, promoItemId, userId] as const,
  itemTypes: (params: PromoCatalogueParams & PageRequest) =>
    [...promotionQueryKeys.all(), 'item-types', params] as const,
  itemTypeList: (params: PromoCatalogueParams) =>
    [...promotionQueryKeys.all(), 'item-types', 'list', params] as const,
  items: (params: PromoItemParams & PageRequest) =>
    [...promotionQueryKeys.all(), 'items', params] as const,
  itemList: (params: PromoItemParams) =>
    [...promotionQueryKeys.all(), 'items', 'list', params] as const,
  assignments: (params: PromoAssignmentParams) =>
    [...promotionQueryKeys.all(), 'assignments', params] as const,
};

export const promoAdjustmentInfiniteQueryOptions = (params: PromoAdjustmentSearch) =>
  infiniteQueryOptions({
    queryKey: promotionQueryKeys.adjustment(params),
    queryFn: async ({ pageParam }): Promise<PromoDistributionResponse> => {
      const res = await apiClient.get(API.promotion.adjustment, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PROMOTION_PAGE_SIZE },
      });
      return promoDistributionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const distributedPromoItemQueryOptions = (promoItemId: number, params: PromoDrillParams) =>
  queryOptions({
    queryKey: promotionQueryKeys.distributedItem(promoItemId, params),
    queryFn: async (): Promise<PromoDistributionRow | null> => {
      const res = await apiClient.get(API.promotion.adjustment, {
        params: { ...params, promoItemId, page: FIRST_PAGE, pageSize: 1 },
      });
      return promoDistributionResponseSchema.parse(res.data).data[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const promoMembersQueryOptions = (promoItemId: number, params: PromoDrillParams) =>
  queryOptions({
    queryKey: promotionQueryKeys.members(promoItemId, params),
    queryFn: async (): Promise<PromoMember[]> => {
      const res = await apiClient.get(API.promotion.adjustmentMembers(promoItemId), { params });
      return promoMemberListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const promoInstitutesQueryOptions = (
  promoItemId: number,
  userId: number,
  { userId: _pickedZmId, ...params }: PromoDrillParams,
) =>
  queryOptions({
    queryKey: promotionQueryKeys.institutes(promoItemId, userId, params),
    queryFn: async (): Promise<PromoInstitute[]> => {
      const res = await apiClient.get(API.promotion.adjustmentInstitutes(promoItemId, userId), {
        params,
      });
      return promoInstituteListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const promoItemTypesQueryOptions = (params: PromoCatalogueParams & PageRequest) =>
  queryOptions({
    queryKey: promotionQueryKeys.itemTypes(params),
    queryFn: async (): Promise<PromoItemTypeResponse> => {
      const res = await apiClient.get(API.promotion.itemTypes, { params });
      return promoItemTypeResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const promoItemTypesInfiniteQueryOptions = (params: PromoCatalogueParams) =>
  infiniteQueryOptions({
    queryKey: promotionQueryKeys.itemTypeList(params),
    queryFn: async ({ pageParam }): Promise<PromoItemTypeResponse> => {
      const res = await apiClient.get(API.promotion.itemTypes, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PROMOTION_PAGE_SIZE },
      });
      return promoItemTypeResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const promoItemsQueryOptions = (params: PromoItemParams & PageRequest) =>
  queryOptions({
    queryKey: promotionQueryKeys.items(params),
    queryFn: async (): Promise<PromoItemResponse> => {
      const res = await apiClient.get(API.promotion.items, { params });
      return promoItemResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const promoItemsInfiniteQueryOptions = (params: PromoItemParams) =>
  infiniteQueryOptions({
    queryKey: promotionQueryKeys.itemList(params),
    queryFn: async ({ pageParam }): Promise<PromoItemResponse> => {
      const res = await apiClient.get(API.promotion.items, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PROMOTION_PAGE_SIZE },
      });
      return promoItemResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const promoAssignmentsInfiniteQueryOptions = (params: PromoAssignmentParams) =>
  infiniteQueryOptions({
    queryKey: promotionQueryKeys.assignments(params),
    queryFn: async ({ pageParam }): Promise<UserPromoResponse> => {
      const res = await apiClient.get(API.promotion.assignments, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PROMOTION_PAGE_SIZE },
      });
      return userPromoResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createPromoItemType(payload: PromoItemTypePayload): Promise<void> {
  await apiClient.post(API.promotion.itemTypes, payload);
}

export async function updatePromoItemType(
  id: number,
  payload: PromoItemTypePayload,
): Promise<void> {
  await apiClient.put(API.promotion.itemType(id), payload);
}

export async function deletePromoItemType(id: number): Promise<void> {
  await apiClient.delete(API.promotion.itemType(id));
}

export async function createPromoItem(payload: PromoItemPayload): Promise<void> {
  await apiClient.post(API.promotion.items, payload);
}

export async function updatePromoItem(id: number, payload: PromoItemPayload): Promise<void> {
  await apiClient.put(API.promotion.item(id), payload);
}

export async function deletePromoItem(id: number): Promise<void> {
  await apiClient.delete(API.promotion.item(id));
}
